import { useEffect, useState } from "react";
import { devWarn } from "@/lib/devLogger";

const FALLBACK_COUNTRY = "IN";
const SESSION_KEY = "gg-detected-country";
const GEO_ENDPOINT = "https://ipapi.co/json/";
const GEO_TIMEOUT_MS = 3000;

/**
 * Detects the visitor's country from their IP address (via a free,
 * keyless geolocation lookup) so the international phone input can
 * default its country selector sensibly instead of always showing
 * India. Falls back to India (+91) -- the Group's home market -- on
 * any failure, timeout, or if the visitor blocks the request.
 *
 * Result (a 2-letter country code, e.g. "IN") is cached in
 * sessionStorage so we don't re-fetch on every form mount within the
 * same visit. This is NOT sensitive/personal data -- it is the same
 * coarse country-level signal already implied by the visitor's own IP
 * address on every request, stored only as a UI convenience, never sent
 * to our backend, and cleared automatically when the tab closes.
 */
export const useDetectedCountry = () => {
  const [country, setCountry] = useState(() => {
    try {
      return sessionStorage.getItem(SESSION_KEY) || null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    if (country) return;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), GEO_TIMEOUT_MS);

    fetch(GEO_ENDPOINT, { signal: controller.signal })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        const code = data?.country_code || data?.country || FALLBACK_COUNTRY;
        setCountry(code);
        try {
          sessionStorage.setItem(SESSION_KEY, code);
        } catch (e) {
          devWarn("Detected country could not be cached in sessionStorage:", e);
        }
      })
      .catch((error) => {
        devWarn("IP-based country detection failed, defaulting to India:", error);
        setCountry(FALLBACK_COUNTRY);
      })
      .finally(() => clearTimeout(timeoutId));

    return () => {
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [country]);

  return country || FALLBACK_COUNTRY;
};
